import {
  Payment,
  type PaymentError,
  type PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import { trackEvent } from "@hopper-b2b/api";
import { NETWORK_CALL_FAILED } from "@hopper-b2b/types";
import { revokeUserAuth } from "./api/revokeUserAuth";

export const NEEDS_NEW_ENROLLMENT_ERROR_CODE = "needs_new_enrollment";
const MAX_REVOKE_ATTEMPTS = 2;
const MAX_ERROR_CODE_LENGTH = 64;
const SAFE_ERROR_CODE_PATTERN = /^[a-z0-9_]+$/;
const REVOKE_TELEMETRY_ROUTE = "nubank_checkout";
const COMPLETED_REVOCATIONS_STORAGE_KEY =
  "hopper.nubank.needsNewEnrollmentRevocations";
// Hard cap on persisted dedup keys per tab. The realistic upper bound is small
// (a handful of distinct payment-error codes per checkout) so 50 leaves plenty
// of headroom while preventing unbounded storage growth.
const MAX_COMPLETED_REVOCATIONS = 50;
// Small jittered back-off between retry attempts to avoid thundering-herd
// against the revoke endpoint when many users hit the same error at once.
const RETRY_BASE_DELAY_MS = 250;
const RETRY_JITTER_MS = 250;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const safeSessionStorage = (): Storage | undefined => {
  try {
    return typeof globalThis !== "undefined" &&
      typeof globalThis.sessionStorage !== "undefined"
      ? globalThis.sessionStorage
      : undefined;
  } catch {
    return undefined;
  }
};

const loadCompletedRevocations = (): Set<string> => {
  const storage = safeSessionStorage();
  if (!storage) {
    return new Set();
  }

  try {
    const raw = storage.getItem(COMPLETED_REVOCATIONS_STORAGE_KEY);
    if (!raw) {
      return new Set();
    }

    const parsed: unknown = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.every((value): value is string => typeof value === "string")
    ) {
      return new Set(parsed);
    }
  } catch {
    // Corrupted storage entry; ignore and start fresh.
  }

  return new Set();
};

const trimToMaxSize = (values: Set<string>, max: number): void => {
  if (values.size <= max) {
    return;
  }

  // Sets preserve insertion order, so dropping from the front evicts the
  // oldest entries first.
  const overflow = values.size - max;
  const iterator = values.values();
  for (let i = 0; i < overflow; i += 1) {
    const next = iterator.next();
    if (next.done) {
      break;
    }
    values.delete(next.value);
  }
};

const persistCompletedRevocations = (values: Set<string>): void => {
  trimToMaxSize(values, MAX_COMPLETED_REVOCATIONS);

  const storage = safeSessionStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(
      COMPLETED_REVOCATIONS_STORAGE_KEY,
      JSON.stringify(Array.from(values))
    );
  } catch {
    // Storage may be full or disabled; deduplication degrades to in-memory only.
  }
};

// Two-tier dedup:
// - attemptedRevocations: in-memory only, marked synchronously before the
//   async revoke starts. This closes the concurrent-caller race window
//   (Strict Mode double-invoke, sibling components, rapid re-renders) without
//   relying on microtask ordering.
// - completedRevocations: persisted to sessionStorage, marked only after a
//   revoke actually succeeds. A transient failure therefore does not lock
//   the user out across page reloads — a reload clears attemptedRevocations
//   and the user gets another at-most-once shot at revoke.
const attemptedRevocations = new Set<string>();
const completedRevocations: Set<string> = loadCompletedRevocations();

export const __resetReEnrollmentRevocationsForTests = (): void => {
  attemptedRevocations.clear();
  completedRevocations.clear();
  const storage = safeSessionStorage();
  if (storage) {
    try {
      storage.removeItem(COMPLETED_REVOCATIONS_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
};

const isNupayPaymentType = (paymentType: Payment): boolean =>
  paymentType === Payment.NupayPayment ||
  paymentType === Payment.EbanxNupayPayment;

type RevokeFailureType = "network" | "client" | "server" | "unknown";

const classifyRevokeFailure = (err: unknown): RevokeFailureType => {
  if (err && typeof err === "object" && "response" in err) {
    const status = (err as { response?: { status?: unknown } }).response
      ?.status;
    if (typeof status === "number") {
      if (status >= 500) {
        return "server";
      }

      if (status >= 400) {
        return "client";
      }
    }
  }

  if (err instanceof Error) {
    return "network";
  }

  return "unknown";
};

const isPaymentErrorLike = (
  error: PurchaseError
): error is PaymentError & {
  value: { type: Payment; value?: unknown };
} => {
  if (!("value" in error)) {
    return false;
  }

  const value = (error as { value?: unknown }).value;
  return typeof value === "object" && value !== null && "type" in value;
};

export const getNupayPaymentErrorCode = (
  error?: PurchaseError
): string | undefined => {
  if (!error || error.Error !== PurchaseErrorEnum.PaymentError) {
    return undefined;
  }

  if (!isPaymentErrorLike(error)) {
    return undefined;
  }

  const paymentError = error;
  if (!isNupayPaymentType(paymentError.value.type)) {
    return undefined;
  }

  const rawCode = paymentError.value.value;
  if (
    typeof rawCode !== "string" ||
    rawCode.length === 0 ||
    rawCode.length > MAX_ERROR_CODE_LENGTH ||
    !SAFE_ERROR_CODE_PATTERN.test(rawCode)
  ) {
    return undefined;
  }

  return rawCode;
};

export const shouldForceNupayReEnrollment = (error?: PurchaseError): boolean =>
  getNupayPaymentErrorCode(error) === NEEDS_NEW_ENROLLMENT_ERROR_CODE;

const MAX_REVOCATION_KEY_LENGTH = 256;
const KNOWN_PURCHASE_ERROR_ENUM_VALUES: ReadonlySet<string> = new Set(
  Object.values(PurchaseErrorEnum)
);
const KNOWN_PAYMENT_ENUM_VALUES: ReadonlySet<string> = new Set(
  Object.values(Payment)
);

const buildRevocationKey = (error: PurchaseError): string | undefined => {
  if (!isPaymentErrorLike(error)) {
    return undefined;
  }

  const code = getNupayPaymentErrorCode(error);
  if (!code) {
    return undefined;
  }

  // Defense-in-depth: only accept enum members we know about so a malformed or
  // version-mismatched API response cannot write arbitrarily long strings into
  // sessionStorage as part of the dedup key.
  if (
    typeof error.Error !== "string" ||
    !KNOWN_PURCHASE_ERROR_ENUM_VALUES.has(error.Error) ||
    typeof error.value.type !== "string" ||
    !KNOWN_PAYMENT_ENUM_VALUES.has(error.value.type)
  ) {
    return undefined;
  }

  // JSON-encode the tuple so the separator cannot collide with any future
  // enum value (e.g. a colon appearing inside Payment or PurchaseErrorEnum).
  const key = JSON.stringify([error.Error, error.value.type, code]);
  if (key.length > MAX_REVOCATION_KEY_LENGTH) {
    return undefined;
  }

  return key;
};

export interface ReEnrollmentRevocationResult {
  shouldForceReEnrollment: boolean;
  attempted: boolean;
  success: boolean;
  attempts: number;
  failureReason?: "revoke_failed_after_retries" | "already_handled";
}

const performRevocationWithRetries =
  async (): Promise<ReEnrollmentRevocationResult> => {
    let failedAttempts = 0;
    let lastFailureType: RevokeFailureType = "unknown";

    for (let attempt = 1; attempt <= MAX_REVOKE_ATTEMPTS; attempt += 1) {
      try {
        await revokeUserAuth({ entity: "HopperInc" });
        return {
          shouldForceReEnrollment: true,
          attempted: true,
          success: true,
          attempts: attempt,
        };
      } catch (err) {
        failedAttempts = attempt;
        lastFailureType = classifyRevokeFailure(err);
        if (attempt < MAX_REVOKE_ATTEMPTS) {
          await sleep(RETRY_BASE_DELAY_MS + Math.random() * RETRY_JITTER_MS);
        }
      }
    }

    trackEvent({
      eventName: NETWORK_CALL_FAILED,
      properties: {
        route: REVOKE_TELEMETRY_ROUTE,
        target: "nupay_reenrollment_revoke",
        attempts: MAX_REVOKE_ATTEMPTS,
        failedAttempts,
        lastFailureType,
        reason: "revoke_failed_after_retries",
      },
    });

    return {
      shouldForceReEnrollment: true,
      attempted: true,
      success: false,
      attempts: MAX_REVOKE_ATTEMPTS,
      failureReason: "revoke_failed_after_retries",
    };
  };

export const revokeEnrollmentForNeedsNewEnrollmentError = (
  error?: PurchaseError
): Promise<ReEnrollmentRevocationResult> => {
  if (!error || !shouldForceNupayReEnrollment(error)) {
    return Promise.resolve({
      shouldForceReEnrollment: false,
      attempted: false,
      success: false,
      attempts: 0,
    });
  }

  const revocationKey = buildRevocationKey(error);
  if (!revocationKey) {
    // Defense-in-depth: shouldForceNupayReEnrollment validated the shape, so
    // this should never happen in practice. If it does, refuse to revoke
    // rather than skipping the dedup guard and risking duplicate calls.
    return Promise.resolve({
      shouldForceReEnrollment: false,
      attempted: false,
      success: false,
      attempts: 0,
    });
  }

  if (
    completedRevocations.has(revocationKey) ||
    attemptedRevocations.has(revocationKey)
  ) {
    return Promise.resolve({
      shouldForceReEnrollment: true,
      attempted: false,
      success: false,
      attempts: 0,
      failureReason: "already_handled",
    });
  }

  // Synchronous in-memory mark closes the concurrent-caller race window
  // before any await. Persistence happens only on success below, so a
  // transient revoke failure does not lock the user across page reloads.
  attemptedRevocations.add(revocationKey);

  return performRevocationWithRetries().then((result) => {
    if (result.success) {
      completedRevocations.add(revocationKey);
      persistCompletedRevocations(completedRevocations);
    }
    return result;
  });
};

export const reportUnexpectedRevokeFailure = (err: unknown): void => {
  trackEvent({
    eventName: NETWORK_CALL_FAILED,
    properties: {
      route: REVOKE_TELEMETRY_ROUTE,
      target: "nupay_reenrollment_revoke_unexpected",
      reason: classifyRevokeFailure(err),
    },
  });
};
